import { TFunction } from 'i18next'
import moment from 'moment'
import { useCallback, useState } from 'react'

import { entityGenerator } from '@/lib/entityGenerator'
import { loadData } from '@/lib/requests/loadData'
import {
	AccommodationType,
	HotelAmenityValues,
	HotelData,
	HotelPaymentOptions,
	HotelResult,
	HotelSelectDetails,
} from '@/travelsuit'
import { UserCurrency } from '@/types/common'
import { HotelCitySearchResult, HotelSearchResult } from '@/types/hotel'

export const emptyHotel = entityGenerator<HotelResult>({
	res_id: '',
	result_index: 0,
	address: '',
	airport_code: '',
	property_amenities: [],
	room_amenities: [],
	id: '',
	currency: UserCurrency.USD,
	is_available: true,
	min_avg_rate: 0,
	min_avg_rate_local_currency: 0,
	min_total: 0,
	min_total_local_currency: 0,
	min_rate_breakfast_included: false,
	in_policy: true,
	policy_deviations: [],
	promo_messages: [],
	tmc_preferred: false,
	tmc_preference_tier: null,
	client_preferred: false,
	client_preference_tier: null,
	warnings: [],
	country_code: '',
	direction: '',
	distance: 0,
	hotel_rating: null,
	iata_guarantee: false,
	latitude: 0,
	longitude: 0,
	name: '',
	phone: null,
	postal_code: '',
	regions: { neighborhood: [] },
	sort_metric: [],
	state_province_code: null,
	thumbnails: [],
	trustyou_rating: null,
	is_bcd_great_rate_hotel: false,
	is_excluded: false,
	chain_info: [],
	check_in_time: '',
	check_out_time: '',
	city: '',
	liked_users: [],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	search: {} as any,
	search_id: 0,
	recommendation_score: 0,
	accommodation_type: AccommodationType.Other,
})

export const emptyHotelData = entityGenerator<HotelData>({
	address: '',
	property_amenities: [],
	room_amenities: [],
	country_code: '',
	star_rating: null,
	rating: null,
	name: '',
	thumbnails: [],
	checkin_time: '',
	checkout_time: '',
	city: '',
	liked_users: [],
	description: '',
	lat: 0,
	long: 0,
	policy: '',
	vendor_hotel_id: '',
	what_to_expect: '',
	area_attraction: '',
	property_type: '',
	has_ecofriendly_badge: false,
	short_description: '',
	dining: '',
	recreation: '',
})

export function prepareRoomJson(hotelId: string, { room, ...details }: HotelSelectDetails, searchId: number) {
	return {
		...details,
		...room,
		hotel_id: hotelId,
		res_id: room.rate_key,
		search_id: searchId,
		cancellation_due_date: room.cancel_by ? moment(room.cancel_by!).format() : null,
	}
}

export function getAmenityTitle(am: HotelAmenityValues, t: TFunction): string {
	const amenityTitleTranslations: Record<HotelAmenityValues, string> = {
		ACND: t('hotels.hotel-amenities.ACND'),
		AIRSH: t('hotels.hotel-amenities.AIRSH'),
		BAR: t('hotels.hotel-amenities.BAR'),
		BCNT: t('hotels.hotel-amenities.BCNT'),
		BFST: t('hotels.hotel-amenities.BFST'),
		CCNT: t('hotels.hotel-amenities.CCNT'),
		CLNG: t('hotels.hotel-amenities.CLNG'),
		FBFST: t('hotels.hotel-amenities.FBFST'),
		FINTT: t('hotels.hotel-amenities.FINTT'),
		FPARK: t('hotels.hotel-amenities.FPARK'),
		FWIFI: t('hotels.hotel-amenities.FWIFI'),
		GOLF: t('hotels.hotel-amenities.GOLF'),
		GYM: t('hotels.hotel-amenities.GYM'),
		INTT: t('hotels.hotel-amenities.INTT'),
		KIDAC: t('hotels.hotel-amenities.KIDAC'),
		KTCH: t('hotels.hotel-amenities.KTCH'),
		LIFT: t('hotels.hotel-amenities.LIFT'),
		LNDR: t('hotels.hotel-amenities.LNDR'),
		MBAR: t('hotels.hotel-amenities.MBAR'),
		MTNG: t('hotels.hotel-amenities.MTNG'),
		NSMK: t('hotels.hotel-amenities.NSMK'),
		PARK: t('hotels.hotel-amenities.PARK'),
		PETS: t('hotels.hotel-amenities.PETS'),
		POOL: t('hotels.hotel-amenities.POOL'),
		RFRG: t('hotels.hotel-amenities.RFRG'),
		RSTR: t('hotels.hotel-amenities.RSTR'),
		RSVC: t('hotels.hotel-amenities.RSVC'),
		SAFE: t('hotels.hotel-amenities.SAFE'),
		SAUNA: t('hotels.hotel-amenities.SAUNA'),
		TENN: t('hotels.hotel-amenities.TENN'),
		TV: t('hotels.hotel-amenities.TV'),
		WCHR: t('hotels.hotel-amenities.WCHR'),
		WIFI: t('hotels.hotel-amenities.WIFI'),
	}

	return amenityTitleTranslations[am]!
}

export function getPaymentOptionsTitle(am: HotelPaymentOptions, t: TFunction): string {
	const paymentOptionsTitleTranslations: Record<HotelPaymentOptions, string> = {
		APO: t('search-builder.hotels.payment-options.all-payment-options'),
		PAP: t('search-builder.hotels.payment-options.pay-at-property'),
		OPP: t('search-builder.hotels.payment-options.online-pre-payment'),
	}

	return paymentOptionsTitleTranslations[am]!
}

export const searchHotelsByName = async (search: string, { include_hotels }: { include_hotels?: boolean } = {}) => {
	const lowerSearch = search.toLocaleLowerCase().trim()
	if (!lowerSearch) {
		return []
	}

	try {
		const { data } = await loadData<(HotelSearchResult & { id: string })[]>({
			resourcePath: 'hotels/search',
			params: { search: lowerSearch, include_hotels },
		})
		return data
	} catch (e) {
		console.error(e)
		return []
	}
}

export async function searchHotelCities(terms: string) {
	const { data } = await loadData<HotelCitySearchResult[]>({
		resourcePath: 'hotels/search_city',
		params: { search: terms },
	})
	return data
}

const hotelCache: Record<string, HotelSearchResult & { id: string }> = {}
const getHotelFactory =
	() =>
	(id: string | undefined): (HotelSearchResult & { id: string }) | undefined =>
		id ? hotelCache[id] || { id, label: id } : undefined

export const useHotelCachedQuery = () => {
	const [getHotelById, setHotelById] = useState(getHotelFactory)

	const queryHotels = useCallback(async (search: string) => {
		const locations = await searchHotelsByName(search, { include_hotels: true })
		const hotels = locations.filter((location) => location.location_type === 'hotel')
		let hasNew = false
		for (const hotel of hotels) {
			if (!hotelCache[hotel.id]) {
				hotelCache[hotel.id] = hotel
				hasNew = true
			}
		}

		if (hasNew) {
			setHotelById(getHotelFactory)
		}

		return hotels
	}, [])

	return {
		queryHotels,
		getHotelById,
	}
}

export const breakfastCodes: HotelAmenityValues[] = ['FBFST', 'BFST']

export type PolicyAccommodationType = Exclude<AccommodationType, AccommodationType.All>

export type AccommodationTypeRule = {
	allowed: PolicyAccommodationType[]
}

export const accommodationTypeSettings: Record<
	AccommodationType,
	{ order: number; getLabel: (t: TFunction) => string }
> = {
	[AccommodationType.All]: {
		order: 0,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.all'),
	},
	[AccommodationType.Apartment]: {
		order: 1,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.apartment'),
	},
	[AccommodationType.BnB]: {
		order: 5,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.bnb'),
	},
	[AccommodationType.ConventionCenter]: {
		order: 6,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.convention-center'),
	},
	[AccommodationType.Economy]: {
		order: 7,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.economy'),
	},
	[AccommodationType.Experience]: {
		order: 8,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.experience'),
	},
	[AccommodationType.Hotel]: {
		order: 4,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.hotel'),
	},
	[AccommodationType.Leisure]: {
		order: 3,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.leisure'),
	},
	[AccommodationType.Other]: {
		order: 9,
		getLabel: (t) => t('hotel-policy-settings.accommodation-type.values.other'),
	},
}
